/**
 * The `image` node's pending state (`061-editor.md` §3, `062-images-assets.md` §3 step 2).
 *
 * "Not a spinner over a grey box — the user should see *their image*, immediately, and
 * merely be told it is still landing." So: the real picture at 60% opacity with an
 * indeterminate bar across its bottom edge, and nothing else moving.
 *
 * `width`/`height` are set on the `<img>` itself so the browser reserves the box from the
 * first frame and the text below does not jump when the real `/uploads/` URL swaps in.
 * Layout shift on the swap is the tell that an app is faking it.
 */
import { NodeViewWrapper, type ReactNodeViewProps } from '@tiptap/react';
import { RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/lib/i18n';
import { retryImageUpload } from '@/features/editor/uploadBridge';

export function ImageNodeView({ node, selected }: ReactNodeViewProps) {
  const { t } = useTranslation();
  const src = typeof node.attrs['src'] === 'string' ? node.attrs['src'] : '';
  const alt = typeof node.attrs['alt'] === 'string' ? node.attrs['alt'] : '';
  const width = typeof node.attrs['width'] === 'number' ? node.attrs['width'] : undefined;
  const height = typeof node.attrs['height'] === 'number' ? node.attrs['height'] : undefined;
  const uploadId = typeof node.attrs['uploadId'] === 'string' ? node.attrs['uploadId'] : null;

  const uploading = node.attrs['uploading'] === true;
  const failed = node.attrs['failed'] === true;

  return (
    <NodeViewWrapper
      as="div"
      data-testid="image-node"
      data-uploading={uploading ? 'true' : 'false'}
      data-failed={failed ? 'true' : 'false'}
      className="relative my-2 inline-block max-w-full"
    >
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        draggable={false}
        className={cn(
          'h-auto max-w-full rounded-sm',
          selected && 'ring-2 ring-[var(--ring)]',
          uploading && 'opacity-60',
          failed && 'opacity-80 outline outline-1 outline-[var(--destructive)]',
        )}
      />

      {uploading && (
        <span
          aria-label={t('editor.image.uploading')}
          className="absolute inset-x-0 bottom-0 h-0.5 overflow-hidden rounded-b-sm bg-[var(--muted)]"
        >
          <span className="block h-full w-1/3 animate-pulse bg-[var(--primary)]" />
        </span>
      )}

      {failed && (
        <span className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 bg-[var(--background)]/90 px-2 py-1 text-xs">
          <span className="text-[var(--muted-foreground)]">{t('editor.image.notUploadedYet')}</span>
          <button
            type="button"
            className="flex items-center gap-1 rounded-sm px-1.5 py-0.5 font-medium hover:bg-[var(--accent)]"
            onClick={() => {
              if (uploadId !== null) void retryImageUpload(uploadId);
            }}
          >
            <RefreshCw className="size-3" />
            {t('common.retry')}
          </button>
        </span>
      )}
    </NodeViewWrapper>
  );
}
